import type { CheckItem, ReplyDraft } from './types';

/**
 * What the draft screens choose behavior and copy by.
 *
 * - `stale_state` carries the draft as the server now holds it, to re-render
 *   from rather than retry.
 * - `must_fix` carries the items to acknowledge and the hash of the text they
 *   were checked on.
 * - `network` means no response arrived, so the request may have landed. Event
 *   batches are safe to resend because the server applies a repeated batch once.
 * - `timeout` is the app giving up on a slow "use draft".
 */
export type ReplyDraftErrorKind =
  | 'stale_state'
  | 'must_fix'
  | 'empty_reply'
  | 'inquiry_closed'
  | 'invalid_status'
  | 'not_found'
  | 'not_opted_in'
  | 'invalid'
  | 'network'
  | 'timeout'
  | 'unknown';

export class ReplyDraftError extends Error {
  constructor(
    public kind: ReplyDraftErrorKind,
    message: string,
    public status?: number,
    public current?: ReplyDraft,
    public mustFix?: { items: CheckItem[]; composedHash: string }
  ) {
    super(message);

    this.name = 'ReplyDraftError';
  }
}

/**
 * Kinds after which the draft can no longer be used from this screen. An
 * invalid status means the draft was sent, discarded or never finished, all
 * of them final, so it closes like a missing one rather than inviting retries.
 */
export function isUnusableDraftError(error: ReplyDraftError): boolean {
  return (
    error.kind === 'not_found' ||
    error.kind === 'not_opted_in' ||
    error.kind === 'inquiry_closed' ||
    error.kind === 'invalid_status'
  );
}
